import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Coupon from "./Coupon";
import CouponListRow from "./CouponListRow";
import CouponModal from "./CouponModal";
import ClaimCelebration from "./ClaimCelebration";
import { INITIAL_COUPONS } from "./couponsData";
import "./Main.css";

/** Newest first in the sheet list; main canvas keeps chronological array order. */
const byReceivedDescThenName = (a, b) => {
  const d = b.received.localeCompare(a.received);
  return d !== 0 ? d : a.name.localeCompare(b.name);
};

/** Fix coupons already saved with a mistaken wall-clock claim date (April 10). */
const normalizeReceivedDate = (iso) => {
  if (!iso || typeof iso !== "string") return iso;
  const m = iso.match(/^(\d{4})-04-10$/);
  return m ? `${m[1]}-04-18` : iso;
};

const formatIsoLongDate = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/** Coupons whose status uses ♾️ (e.g. Animal Meme) stay claimable forever. */
const isUnlimitedCoupon = (c) =>
  Boolean(c?.status && /♾️/.test(String(c.status)));

const todayIsoLocal = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isFutureCoupon = (coupon, todayIso = todayIsoLocal()) =>
  coupon?.status !== "Claimed" &&
  typeof coupon?.received === "string" &&
  coupon.received > todayIso;

const toDisplayCoupon = (coupon, todayIso = todayIsoLocal()) =>
  isFutureCoupon(coupon, todayIso)
    ? {
        ...coupon,
        emoji: "🔒",
        name: "Locked",
        description: `Available on ${formatIsoLongDate(coupon.received)}`,
      }
    : coupon;

const Main = () => {
  const [couponsState, setCouponsState] = useState(() =>
    INITIAL_COUPONS.map((c) => ({
      ...c,
      received: normalizeReceivedDate(c.received),
    }))
  );
  const [selectedCouponName, setSelectedCouponName] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [listEntered, setListEntered] = useState(false);
  /** Bumped after resize settles so coupons re-spread like a fresh load. */
  const [relayoutKey, setRelayoutKey] = useState(0);
  const [celebration, setCelebration] = useState(null);
  const todayIso = todayIsoLocal();

  const selectedCoupon = selectedCouponName
    ? couponsState.find((c) => c.name === selectedCouponName) ?? null
    : null;
  const selectedCouponIsFuture = isFutureCoupon(selectedCoupon, todayIso);

  const openModal = (coupon) => {
    setSelectedCouponName(coupon.name);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCouponName(null);
    setIsModalOpen(false);
  };

  const handleClaim = () => {
    if (!selectedCouponName) return;
    const coupon = couponsState.find((c) => c.name === selectedCouponName);
    const emoji = coupon?.emoji;
    const unlimited = isUnlimitedCoupon(coupon);
    setCouponsState((prev) =>
      prev.map((c) =>
        c.name === selectedCouponName
          ? unlimited
            ? c
            : {
                ...c,
                status: "Claimed",
                active: false,
              }
          : c
      )
    );
    closeModal();
    if (emoji && coupon?.name) {
      setCelebration({ emoji, name: coupon.name });
    }
  };

  useEffect(() => {
    if (!isListOpen) {
      setListEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setListEntered(true));
    });
    return () => cancelAnimationFrame(id);
  }, [isListOpen]);

  useEffect(() => {
    let timeoutId;
    const onResize = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setRelayoutKey((k) => k + 1);
      }, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const closeList = () => setListEntered(false);

  const handleSheetTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "transform") return;
    if (listEntered) return;
    setIsListOpen(false);
  };

  const claimedCoupons = couponsState
    .filter((c) => c.status === "Claimed")
    .sort(byReceivedDescThenName);
  const availableCoupons = couponsState
    .filter((c) => c.status !== "Claimed")
    .sort(byReceivedDescThenName);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="header">Lindsay's Coupons</div>
      <div className="main">
        {couponsState.map((coupon, index) => (
          (() => {
            const displayCoupon = toDisplayCoupon(coupon, todayIso);
            return (
          <Coupon
            key={`${coupon.name}-${index}`}
            emoji={displayCoupon.emoji}
            name={displayCoupon.name}
            active={coupon.active}
            description={displayCoupon.description}
            relayoutKey={relayoutKey}
            onClick={() => openModal(coupon)}
          />
            );
          })()
        ))}
        <CouponModal
          isOpen={isModalOpen}
          emoji={toDisplayCoupon(selectedCoupon, todayIso)?.emoji}
          name={toDisplayCoupon(selectedCoupon, todayIso)?.name}
          description={toDisplayCoupon(selectedCoupon, todayIso)?.description}
          status={selectedCoupon?.status}
          received={selectedCouponIsFuture ? null : selectedCoupon?.received}
          showBelowLabels={!selectedCouponIsFuture}
          disableClaimButton={selectedCouponIsFuture}
          onClose={closeModal}
          onClaim={handleClaim}
        />
        {celebration ? (
          <ClaimCelebration
            emoji={celebration.emoji}
            name={celebration.name}
            onClose={() => setCelebration(null)}
          />
        ) : null}
        <button
          type="button"
          className="coupon-list-fab"
          aria-label="Show coupons"
          aria-expanded={isListOpen}
          onClick={() => setIsListOpen(true)}
        >
          <span className="coupon-list-fab-icon" aria-hidden>
            <span />
            <span />
            <span />
          </span>
        </button>
        {isListOpen && (
          <div
            className={
              listEntered
                ? "coupon-list-overlay coupon-list-overlay--open"
                : "coupon-list-overlay"
            }
            role="dialog"
            aria-modal="true"
            aria-label="Coupons"
            onClick={closeList}
          >
            <div
              className="coupon-list-panel"
              onClick={(e) => e.stopPropagation()}
              onTransitionEnd={handleSheetTransitionEnd}
            >
              <div className="coupon-list-grabber" aria-hidden />
              <div className="coupon-list-header">
                <h2 className="coupon-list-title">Coupons</h2>
                <button
                  type="button"
                  className="coupon-list-close"
                  aria-label="Close list"
                  onClick={closeList}
                >
                  ✕
                </button>
              </div>
              <div className="coupon-list-scroll">
                {availableCoupons.length > 0 && (
                  <ul className="coupon-list">
                    {availableCoupons.map((coupon, index) => (
                      <CouponListRow
                        key={`${coupon.name}-${index}`}
                        coupon={toDisplayCoupon(coupon, todayIso)}
                        onClick={() => openModal(coupon)}
                      />
                    ))}
                  </ul>
                )}
                {claimedCoupons.length > 0 && (
                  <section className="coupon-list-section" aria-label="Claimed">
                    <h3 className="coupon-list-section-title">Claimed</h3>
                    <ul className="coupon-list">
                      {claimedCoupons.map((coupon, index) => (
                        <CouponListRow
                          key={`${coupon.name}-${index}`}
                          coupon={toDisplayCoupon(coupon, todayIso)}
                          onClick={() => openModal(coupon)}
                        />
                      ))}
                    </ul>
                  </section>
                )}
                <p className="coupon-list-footer">With ❤️ from Sam</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default Main;

